import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import useReveal from "../Components/useReveal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faPhone,
  faCalendarCheck,
  faComments,
  faClipboardList,
  faUserCheck,
  faHeartPulse,
  faHandHoldingHeart,
  faShieldHeart,
  faClock,
  faHouseUser,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import "../Styles/Services.css";
import { SERVICES } from "../Scripts/servicesData";

const services = SERVICES;

const steps = [
  {
    icon: faPhone,
    title: "Reach Out",
    text: "Call us or request a quote. We listen to your needs and answer your questions.",
  },
  {
    icon: faComments,
    title: "Free Consultation",
    text: "We discuss your situation, preferences and goals for care.",
  },
  {
    icon: faClipboardList,
    title: "Custom Care Plan",
    text: "We build a personalized plan tailored to your daily routines.",
  },
  {
    icon: faUserCheck,
    title: "Start Care",
    text: "A matched, compassionate caregiver begins supporting you at home.",
  },
];

const benefits = [
  {
    icon: faHouseUser,
    title: "Comfort of Home",
    text: "Care delivered where you feel safest and most independent.",
  },
  {
    icon: faShieldHeart,
    title: "Trusted Caregivers",
    text: "Compassionate, screened professionals who truly care.",
  },
  {
    icon: faHeartPulse,
    title: "Holistic Support",
    text: "Physical, emotional and social well-being all together.",
  },
  {
    icon: faClock,
    title: "Flexible Scheduling",
    text: "Support available between 6:00 AM and 10:30 PM.",
  },
  {
    icon: faHandHoldingHeart,
    title: "Personalized Plans",
    text: "Every plan is shaped around your unique needs.",
  },
  {
    icon: faUserCheck,
    title: "Family Peace of Mind",
    text: "Reliable updates and support for the whole family.",
  },
];

function Services() {
  useReveal();

  return (
    <div className="services-page">
      <Navbar />

      <section className="page-banner">
        <div className="hr-container">
          <span className="hr-eyebrow">Our Services</span>
          <h1>Comprehensive Care Solutions</h1>
          <p>
            Nonmedical in-home care designed around each client, promoting
            independence, comfort and a better quality of life.
          </p>
          <div className="page-crumb">
            <Link to="/">Home</Link> / Services
          </div>
        </div>
      </section>

      {/* Services list */}
      <section className="hr-section">
        <div className="hr-container">
          <div className="hr-center reveal">
            <span className="hr-eyebrow">What We Offer</span>
            <h2 className="hr-heading">
              Care <span className="accent">Tailored</span> to Every Need
            </h2>
          </div>
          <div className="svc-grid" style={{ marginTop: "50px" }}>
            {services.map((s, i) => (
              <article className="svc-card reveal" key={s.title}>
                <Link to={`/services/${s.slug}`} className="svc-media">
                  <img src={s.img} alt={s.title} />
                  <div className="svc-card-num">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </Link>
                <div className="svc-card-body">
                  <h2>
                    <Link to={`/services/${s.slug}`} className="svc-title-link">
                      {s.title}
                    </Link>
                  </h2>
                  <p>{s.intro}</p>
                  <div className="svc-tags">
                    {s.included.slice(0, 3).map((item) => (
                      <span className="svc-tag" key={item.title}>
                        <FontAwesomeIcon icon={faCheck} /> {item.title}
                      </span>
                    ))}
                  </div>
                  <Link to={`/services/${s.slug}`} className="svc-learn-more">
                    Learn More <FontAwesomeIcon icon={faArrowRight} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="hr-section bg-cream">
        <div className="hr-container">
          <div className="hr-center reveal">
            <span className="hr-eyebrow">How It Works</span>
            <h2 className="hr-heading">Getting Started Is Simple</h2>
            <p className="hr-subheading">
              From your first call to ongoing care, we make the journey easy and
              reassuring.
            </p>
          </div>
          <div className="process-grid">
            {steps.map((s, i) => (
              <div className="process-step reveal" key={s.title}>
                <div className="ps-circle">
                  <FontAwesomeIcon icon={s.icon} />
                  <span className="ps-num">{i + 1}</span>
                </div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="hr-section">
        <div className="hr-container">
          <div className="hr-center reveal">
            <span className="hr-eyebrow">Why Families Choose Us</span>
            <h2 className="hr-heading">
              The <span className="accent">Harmony Rise</span> Difference
            </h2>
          </div>
          <div className="benefits-grid">
            {benefits.map((b) => (
              <div className="benefit reveal" key={b.title}>
                <span className="b-icon">
                  <FontAwesomeIcon icon={b.icon} />
                </span>
                <div>
                  <h4>{b.title}</h4>
                  <p>{b.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="hr-section" style={{ paddingTop: 0 }}>
        <div className="hr-container">
          <div className="cta-band reveal">
            <h2>Ready to Care?</h2>
            <p>Connect with us for personalized support tailored to you.</p>
            <div className="cta-actions">
              <Link to="/book-appointment" className="hr-btn hr-btn-white hr-btn-lg">
                <FontAwesomeIcon icon={faCalendarCheck} /> Book An Appointment
              </Link>
              <a href="tel:2535230098" className="hr-btn hr-btn-ghost hr-btn-lg">
                <FontAwesomeIcon icon={faPhone} /> Call: 253-523-0098
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Services;
